import React from 'react';
import { Box, Button, Stack, useTheme } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { layoutGap } from '../../../theme/theme';
import { getWorkflowWorkspacePalette } from './WorkflowWorkspacePrimitives';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import type { HelpValue } from '../common/helpModel';

export interface WorkspaceCompactSectionBarSection<Key extends string = string> {
  key: Key;
  label: string;
  tooltip?: HelpValue;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
  'data-testid'?: string;
  'aria-label'?: string;
}

export interface WorkspaceCompactSectionBarProps<Key extends string = string> {
  sections: WorkspaceCompactSectionBarSection<Key>[];
  activeSection: Key;
  onChange: (key: Key) => void;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  containerDataTestId?: string;
  sx?: SxProps<Theme>;
}

export default function WorkspaceCompactSectionBar<Key extends string = string>({
  sections,
  activeSection,
  onChange,
  containerRef,
  containerDataTestId,
  sx,
}: WorkspaceCompactSectionBarProps<Key>) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);

  return (
    <Box
      ref={containerRef}
      data-testid={containerDataTestId}
      sx={{
        px: 1.1,
        py: 0.7,
        borderBottom: `1px solid ${palette.paneDivider}`,
        backgroundColor: palette.sectionRaisedBackground,
        ...sx,
      }}
    >
      <Stack direction="row" spacing={layoutGap.blend} flexWrap="wrap" useFlexGap>
        {sections.map((section) => {
          const button = (
            <Button
              ref={section.buttonRef}
              data-testid={section['data-testid']}
              size="small"
              variant={activeSection === section.key ? 'contained' : 'outlined'}
              aria-label={section['aria-label']}
              onClick={() => onChange(section.key)}
            >
              {section.label}
            </Button>
          );

          return section.tooltip ? (
            <ActionHelpTooltip key={section.key} help={section.tooltip} placement="bottom">
              {button}
            </ActionHelpTooltip>
          ) : (
            <React.Fragment key={section.key}>{button}</React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}
